import { api } from './client.ts';

export interface ScanEnqueueResponse {
  scanId: string;
  status: string;
}

/**
 * Anonymous scans started from the public homepage return an extra accessToken — the
 * SPA must keep this around (URL hash + localStorage) and pass it on every subsequent
 * request via the `?token=` query string or `X-Scan-Access-Token` header.
 *
 * viewMode reflects the email-collection state at enqueue time:
 *   - "anonymous_locked"   — visitor skipped the optional email field; on-page gate will appear
 *   - "anonymous_unlocked" — visitor supplied email upfront; no in-page gate, report emailed on completion
 */
export interface AnonymousScanEnqueueResponse extends ScanEnqueueResponse {
  accessToken: string;
  viewMode: 'anonymous_locked' | 'anonymous_unlocked';
}

export type ComplianceTier = 'Bronze' | 'Silver' | 'Gold';
export type DomainVerificationMethod = 'DnsTxt' | 'MetaTag';

/**
 * "owned"               — signed-in user looking at their own scan; full report
 * "anonymous_unlocked"  — anonymous visitor who already submitted email; full report
 * "anonymous_locked"    — anonymous visitor pre-email; summary + email gate, top 3 violations only
 */
export type ScanViewMode = 'owned' | 'anonymous_unlocked' | 'anonymous_locked';

export interface ScanDetail {
  id: string;
  targetUrl: string;
  status: string;
  viewMode: ScanViewMode;
  isAnonymous: boolean;
  anonymousEmail: string | null;
  anonymousEmailSubmittedAt: string | null;
  complianceScore: number;
  criticalCount: number;
  seriousCount: number;
  moderateCount: number;
  minorCount: number;
  pageTitle: string | null;
  completedAt: string | null;
  errorMessage: string | null;
  progressMessage: string | null;
  progressCurrent: number;
  progressTotal: number;
  totalViolations: number;
  verificationToken: string | null;
  tier: ComplianceTier | null;
  badgeIssued: boolean;
  badgeIssuedAt: string | null;
  domainVerifiedAt: string | null;
  domainVerificationMethod: DomainVerificationMethod | null;
  hasCertificatePdf: boolean;
  violations: Array<{
    ruleId: string;
    impact: string;
    wcag: string;
    pageUrl: string | null;
    selector: string | null;
    shortDescription: string;
    plainEnglishExplanation: string | null;
    eaaSection: string | null;
    eaaQuote: string | null;
  }>;
}

export interface ScanListItem {
  id: string;
  targetUrl: string;
  status: string;
  complianceScore: number;
  criticalCount: number;
  seriousCount: number;
  moderateCount: number;
  minorCount: number;
  pageTitle: string | null;
  completedAt: string | null;
  createdAt: string;
  hasReport: boolean;
  hasStatement: boolean;
}

export interface ScanListResponse {
  items: ScanListItem[];
  total: number;
}

export const scansApi = {
  async create(url: string): Promise<ScanEnqueueResponse> {
    return api<ScanEnqueueResponse>('/api/scans', { method: 'POST', body: { url } });
  },

  /**
   * Email is optional. When supplied (upfront-email flow), the scan is enqueued with the
   * email already recorded — the report email goes out automatically on completion and
   * the on-page email gate is skipped. When omitted, the visitor sees the in-page gate
   * after the scan completes (results-then-email).
   */
  async createAnonymous(
    url: string,
    options: { email?: string; marketingConsent?: boolean } = {},
  ): Promise<AnonymousScanEnqueueResponse> {
    return api<AnonymousScanEnqueueResponse>('/api/scans/anonymous', {
      method: 'POST',
      body: {
        url,
        email: options.email ?? null,
        marketingConsent: options.marketingConsent ?? false,
      },
    });
  },

  /** Submits the visitor email for an anonymous scan, unlocking the PDF + full report. */
  async submitEmailForReport(
    scanId: string,
    accessToken: string,
    email: string,
    marketingConsent: boolean,
  ): Promise<void> {
    await api(`/api/scans/${scanId}/email-report`, {
      method: 'POST',
      body: { email, marketingConsent },
      headers: { 'X-Scan-Access-Token': accessToken },
    });
  },

  async list(take = 50, skip = 0): Promise<ScanListResponse> {
    return api<ScanListResponse>(`/api/scans?take=${take}&skip=${skip}`);
  },

  /** accessToken is only required for anonymous scans; pass undefined for owned ones. */
  async get(id: string, accessToken?: string): Promise<ScanDetail> {
    return api<ScanDetail>(`/api/scans/${id}`, {
      headers: accessToken ? { 'X-Scan-Access-Token': accessToken } : undefined,
    });
  },

  /**
   * Anonymous downloads need the token in the URL because the browser's <a download> can't
   * attach a header. Owned downloads ignore the query param (cookie auth wins).
   */
  reportPdfUrl(id: string, accessToken?: string): string {
    return accessToken
      ? `/api/scans/${id}/report.pdf?token=${encodeURIComponent(accessToken)}`
      : `/api/scans/${id}/report.pdf`;
  },

  statementHtmlUrl(id: string, accessToken?: string): string {
    return accessToken
      ? `/api/scans/${id}/statement.html?token=${encodeURIComponent(accessToken)}`
      : `/api/scans/${id}/statement.html`;
  },

  async issueBadge(id: string): Promise<void> {
    await api(`/api/scans/${id}/issue-badge`, { method: 'POST' });
  },

  async verifyDomain(id: string, method: DomainVerificationMethod): Promise<void> {
    await api(`/api/scans/${id}/verify-domain`, { method: 'POST', body: { method } });
  },
};

// Public verifier — anyone can call these by token, no auth required.
export interface VerifiableScanPayload {
  token: string;
  targetUrl: string;
  complianceScore: number;
  tier: ComplianceTier;
  tierLabel: string;
  completedAt: string;
  issuedAt: string;
  criticalCount: number;
  seriousCount: number;
  moderateCount: number;
  minorCount: number;
  domainVerifiedAt: string | null;
  domainVerificationMethod: string | null;
  hasCertificatePdf: boolean;
}

export const verifyApi = {
  async get(token: string): Promise<VerifiableScanPayload> {
    return api<VerifiableScanPayload>(`/api/verify/${encodeURIComponent(token)}`);
  },

  badgeImageUrl(token: string): string {
    return `/api/verify/${encodeURIComponent(token)}/badge.svg`;
  },

  certificatePdfUrl(token: string): string {
    return `/api/verify/${encodeURIComponent(token)}/certificate.pdf`;
  },

  verifierUrl(token: string, origin?: string): string {
    const base = origin ?? (typeof window !== 'undefined' ? window.location.origin : 'https://eaaproof.com');
    return `${base}/verify/${encodeURIComponent(token)}`;
  },
};
